import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import api from "@/lib/axios"
import { VerSklRekap } from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { GiBackwardTime } from "react-icons/gi"
import { MdOutlineSync } from "react-icons/md"
import { useMutation, useQueryClient } from "@tanstack/react-query"

const postData = async (newData: {
  id_verskl_rekap: string
  total_peserta: number
  jumlah_kelompok: number
}) => {
  const response = await api.post(
    "/setting/verifikator-skl/buat-kelompok",
    newData
  )
  return response.data
}

const deleteData = async ({ id_verskl_rekap }: { id_verskl_rekap: string }) => {
  const response = await api.delete("/setting/verifikator-skl/reset-kelompok", {
    data: { id_verskl_rekap },
  })
  return response.data
}

export default function FormKelompok({
  closeModal,
  selectedRow,
  method,
}: {
  closeModal: () => void
  selectedRow: VerSklRekap | null
  method: "POST" | "DELETE"
}) {
  const [form, setForm] = useState({
    id_verskl_rekap: selectedRow?.id_verskl_rekap || "",
    total_peserta: selectedRow?.total_peserta || 0,
    jumlah_kelompok: 0,
  })

  const isPost = method === "POST"
  const isDelete = method === "DELETE"

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isDelete ? deleteData : undefined,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ["listRekapPesertaSkl"],
      })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete) {
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<GiBackwardTime />}
          title={"Reset Kelompok"}
          description={`Reset jumlah kelompok yang telah dibuat.`}
        />
        <div className="mt-3 flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            onClick={() => closeModal()}
            disabled={isPending}
          >
            Batal
          </Button>
          <Button className="flex items-center gap-3" disabled={isPending}>
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Reset"
            )}
          </Button>
        </div>
      </form>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={<MdOutlineSync />}
        title={"Buat Kelompok"}
        description={`Dari ${form.total_peserta} peserta akan dibagi jumlah kelompok dan hasilnya adalah jumlah peserta yang akan diverifikasi oleh verifikator`}
      />
      <div>
        <Label>Total Peserta</Label>
        <Input readOnly disabled value={form.total_peserta} />
      </div>

      <div>
        <Label>Jumlah Kelompok</Label>
        <Input
          placeholder="Ketik jumlah kelompok (contoh: 10)"
          value={form.jumlah_kelompok === 0 ? "" : form.jumlah_kelompok}
          onChange={(event) =>
            setForm((prev) => ({
              ...prev,
              jumlah_kelompok: Number(event.target.value),
            }))
          }
        />
      </div>

      <div className="mt-3 flex gap-3">
        <Button
          type="button"
          variant={"outline"}
          onClick={() => closeModal()}
          disabled={isPending}
        >
          Batal
        </Button>
        <Button className="flex items-center gap-3" disabled={isPending}>
          {isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              Processing...
            </>
          ) : (
            "Simpan"
          )}
        </Button>
      </div>
    </form>
  )
}
